// SAD-900: search, filter and sort for deliverable and date tables.
// Pure helpers so every list (program, team, duty dates, awaiting approval)
// shares one reading of "matches CLIN 0001AE" and one sort order.
import { dutyClinDisplay, dutyKeyMatchesClinQuery, familyMembers } from './dutyClin';
/** Blank / missing workstream id = contract-wide (matches teamName / Team column). */ export function normalizeTeamId(id) {
    return (id ?? '').trim();
}
/**
 * The one team every given date belongs to, else "" (contract-wide): the Log
 * event drawer's default when a caller knows the dates but not a team (SAD-963).
 */ export function soleTeam(workstreamIds) {
    const ids = new Set(workstreamIds.map(normalizeTeamId));
    const [only] = ids;
    return ids.size === 1 && only ? only : '';
}
/** Inclusive calendar-day span; empty start/end means open on that side. */ export function dateInSpan(day, start, end) {
    const d = (day ?? '').trim();
    if (!d) return false;
    const s = (start ?? '').trim();
    const e = (end ?? '').trim();
    if (s && d < s) return false;
    if (e && d > e) return false;
    return true;
}
/**
 * A date's period for filters: the stored periodOfPerformanceId when set,
 * else the program PoP whose span contains its nominal day (the day it
 * belongs to; a roll past the period's end does not move it into the next),
 * else its dueOn. When still unset and the date sits on an exercise /
 * optional-task window, use the PoP that contains that exercise's start (OT
 * dates often ship with a null period id).
 */ export function resolveOccurrencePeriodId(occ, periods, exercises = []) {
    const stored = (occ.periodOfPerformanceId ?? '').trim();
    if (stored) return stored;
    const due = (occ.nominalDueOn || occ.dueOn || '').trim();
    if (!due) return '';
    const byDue = periods.find((p)=>dateInSpan(due, p.start, p.end));
    if (byDue) return byDue.id;
    const exId = (occ.exerciseId ?? '').trim();
    const ex = (exId ? exercises.find((e)=>e.id === exId) : undefined) ?? exercises.find((e)=>dateInSpan(due, e.start, e.end));
    if (!ex) return '';
    const anchor = (ex.start || due).trim();
    return periods.find((p)=>dateInSpan(anchor, p.start, p.end))?.id ?? '';
}
/** Unique resolved period ids for a duty's dates (null ids derived from dueOn). */ export function periodIdsForOccurrences(occs, periods, exercises = []) {
    return uniqueSorted(occs.map((o)=>resolveOccurrencePeriodId(o, periods, exercises)));
}
function setHas(set, id) {
    if (!set) return false;
    if (Array.isArray(set)) return set.includes(id);
    return set.has(id);
}
function clinSortKey(dutyKey, clins) {
    return dutyClinDisplay(dutyKey, clins).label || dutyKey;
}
/** Text match: title, PWS, duty key / CLIN family members. */ export function matchesDeliverableSearch(row, query, clins) {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    if (row.title.toLowerCase().includes(q)) return true;
    if (row.pwsRef.toLowerCase().includes(q)) return true;
    if ((row.letter ?? '').toLowerCase().includes(q)) return true;
    return dutyKeyMatchesClinQuery(row.dutyKey, query, clins);
}
/** Whether a concrete CLIN filter value hits this duty (family or single). */ export function matchesClinFilter(dutyKey, clinFilter, clins) {
    const f = clinFilter.trim();
    if (!f) return true;
    const display = dutyClinDisplay(dutyKey, clins);
    if (display.label.toLowerCase() === f.toLowerCase()) return true;
    if (dutyKey.toLowerCase() === f.toLowerCase()) return true;
    // Concrete member ("0001AE") matches the family duty.
    if (familyMembers(dutyKey, clins).some((m)=>m.toLowerCase() === f.toLowerCase())) return true;
    return dutyKeyMatchesClinQuery(dutyKey, f, clins);
}
export function filterDeliverableRows(rows, filters, clins) {
    const q = (filters.q ?? '').trim();
    const clin = (filters.clin ?? '').trim();
    const pws = (filters.pws ?? '').trim();
    const team = (filters.team ?? '').trim();
    const status = (filters.status ?? '').trim();
    const sourceDoc = (filters.sourceDoc ?? '').trim();
    const period = (filters.period ?? '').trim();
    return rows.filter((r)=>{
        if (q && !matchesDeliverableSearch(r, q, clins)) return false;
        if (clin && !matchesClinFilter(r.dutyKey, clin, clins)) return false;
        if (pws && r.pwsRef.trim() !== pws) return false;
        if (team) {
            const want = team === 'contract-wide' ? '' : normalizeTeamId(team);
            // Match the Team column: blank / missing workstream id = contract-wide.
            const ids = [
                ...Array.isArray(r.teamIds) ? r.teamIds : r.teamIds ?? []
            ].map(normalizeTeamId);
            if (!ids.includes(want)) return false;
        }
        if (status && (r.statusState ?? '') !== status) return false;
        if (sourceDoc) {
            const id = (r.sourceDocumentId ?? '').trim();
            if (sourceDoc === 'none' ? id !== '' : id !== sourceDoc) return false;
        }
        if (period && !setHas(r.periodIds, period)) return false;
        return true;
    });
}
function cmpStr(a, b, dir) {
    const c = a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base'
    });
    return dir === 'asc' ? c : -c;
}
export function sortDeliverableRows(rows, sort, clins) {
    if (!sort) return rows;
    const { key, dir } = sort;
    return [
        ...rows
    ].sort((a, b)=>{
        switch(key){
            case 'title':
                return cmpStr(a.title, b.title, dir);
            case 'clin':
                return cmpStr(a.clinLabel ?? clinSortKey(a.dutyKey, clins), b.clinLabel ?? clinSortKey(b.dutyKey, clins), dir);
            case 'pws':
                return cmpStr(`${a.pwsRef} ${a.letter ?? ''}`.trim(), `${b.pwsRef} ${b.letter ?? ''}`.trim(), dir);
            case 'next':
                {
                    // Blank next-due rows always sort last (asc and desc).
                    const aBlank = !(a.nextDueOn ?? '').trim();
                    const bBlank = !(b.nextDueOn ?? '').trim();
                    if (aBlank !== bBlank) return aBlank ? 1 : -1;
                    return cmpStr(a.nextDueOn || '', b.nextDueOn || '', dir);
                }
            case 'status':
                return cmpStr(a.statusState || 'zzz', b.statusState || 'zzz', dir);
            default:
                return 0;
        }
    });
}
export function filterDateRows(rows, filters, clins = []) {
    const q = (filters.q ?? '').trim().toLowerCase();
    const clin = (filters.clin ?? '').trim().toLowerCase();
    const team = (filters.team ?? '').trim();
    const status = (filters.status ?? '').trim();
    const period = (filters.period ?? '').trim();
    const sourceDoc = (filters.sourceDoc ?? '').trim();
    const pws = (filters.pws ?? '').trim();
    return rows.filter((r)=>{
        if (q) {
            const hay = [
                r.title ?? '',
                r.clinLabel,
                r.teamName,
                r.pwsRef ?? '',
                r.dutyKey ?? ''
            ].join(' ').toLowerCase();
            const textOk = hay.includes(q);
            const clinOk = r.dutyKey ? dutyKeyMatchesClinQuery(r.dutyKey, q, clins) : false;
            if (!textOk && !clinOk) return false;
        }
        if (clin && r.clinLabel.toLowerCase() !== clin && !(r.dutyKey && matchesClinFilter(r.dutyKey, clin, clins))) return false;
        if (team) {
            const want = team === 'contract-wide' ? '' : normalizeTeamId(team);
            if (normalizeTeamId(r.teamId) !== want) return false;
        }
        if (status && (r.statusState ?? '') !== status) return false;
        if (period && (r.periodId ?? '').trim() !== period) return false;
        if (sourceDoc) {
            const id = (r.sourceDocumentId ?? '').trim();
            if (sourceDoc === 'none' ? id !== '' : id !== sourceDoc) return false;
        }
        if (pws && (r.pwsRef ?? '').trim() !== pws) return false;
        return true;
    });
}
export function sortDateRows(rows, sort) {
    if (!sort) return rows;
    const { key, dir } = sort;
    return [
        ...rows
    ].sort((a, b)=>{
        switch(key){
            case 'due':
                return cmpStr(a.dueOn || '9', b.dueOn || '9', dir);
            case 'clin':
                return cmpStr(a.clinLabel || 'zzz', b.clinLabel || 'zzz', dir);
            case 'team':
                return cmpStr(a.teamName, b.teamName, dir);
            case 'status':
                return cmpStr(a.statusState || 'zzz', b.statusState || 'zzz', dir);
            case 'title':
                return cmpStr(a.title || '', b.title || '', dir);
            default:
                return 0;
        }
    });
}
/** Unique filter options from a list of rows, sorted. */ export function uniqueSorted(values) {
    return [
        ...new Set([
            ...values
        ].map((v)=>v.trim()).filter(Boolean))
    ].sort((a, b)=>a.localeCompare(b, undefined, {
            numeric: true,
            sensitivity: 'base'
        }));
}
/** Toggle / cycle sort on a column: none → asc → desc → asc. */ export function nextSort(current, columnId) {
    if (!current || current.key !== columnId) return {
        key: columnId,
        dir: 'asc'
    };
    return {
        key: columnId,
        dir: current.dir === 'asc' ? 'desc' : 'asc'
    };
}
/** SpecraDataTable uses ascending/descending; helpers use asc/desc. */ export function toUiSortDir(dir) {
    return dir === 'asc' ? 'ascending' : 'descending';
}
export function fromUiSortDir(dir) {
    return dir === 'ascending' ? 'asc' : 'desc';
}
/** CLIN filter option labels for a set of duty keys (compact family labels). */ export function clinFilterOptions(dutyKeys, clins) {
    const labels = [];
    for (const key of dutyKeys){
        const { label } = dutyClinDisplay(key, clins);
        if (label) labels.push(label);
    }
    return uniqueSorted(labels);
}
